import React from 'react';
import Modal from '../../../../components/Modal';
import WorkflowStatusPill from '../../../../components/WorkflowStatusPill';
import { CheckIcon } from '../../../../components/IconButton/icons';
import { WorkflowItem, WorkflowStatus, workflowItemNames } from '../../config';
import { MANAGER_ONLY_LABEL } from '../../../../utils/workflowStatusAccess';
import './move-to-modal.scss';

// =======================================
// INTERFACES
// =======================================

interface MoveToModalProps {
  item: WorkflowItem | null;
  statuses: WorkflowStatus[];
  /** A column this user may not move a job into (manager-only statuses). */
  isLocked?: (status: WorkflowStatus) => boolean;
  onClose: () => void;
  onMove: (item: WorkflowItem, statusId: number) => void;
}

// =======================================
// MAIN COMPONENT
// =======================================

/**
 * Move a card to another column without dragging it: the tablet and keyboard
 * path, where carrying a card across nine columns is the hard way. Lists the
 * board's columns with the card's current one ticked.
 */
const MoveToModal: React.FC<MoveToModalProps> = ({ item, statuses, isLocked, onClose, onMove }) => {
  const names = item ? workflowItemNames(item) : null;
  const subject = names ? [names.title, names.stageLine].filter(Boolean).join(' · ') : '';
  const currentId = item
    ? (item.workflow_status_id && statuses.some(s => s.id === item.workflow_status_id)
        ? item.workflow_status_id
        : statuses[0]?.id)
    : null;

  return (
    <Modal
      isOpen={item !== null}
      onClose={onClose}
      size="sm"
      title={item ? `Move ${item.job_number}` : ''}
      className="move-to-modal"
    >
      {item && (
        <>
          <p className="move-to-modal__subject" title={subject}>{subject}</p>
          <ul className="move-to-modal__list">
            {statuses.map(status => {
              const isCurrent = status.id === currentId;
              // Shown, not hidden: a column missing from the list reads as a bug.
              const locked = !isCurrent && Boolean(isLocked?.(status));
              return (
                <li key={status.id}>
                  <button
                    type="button"
                    className={`move-to-modal__option ${isCurrent ? 'is-current' : ''} ${locked ? 'is-locked' : ''}`.trim()}
                    aria-current={isCurrent || undefined}
                    disabled={isCurrent || locked}
                    onClick={() => onMove(item, status.id)}
                  >
                    <WorkflowStatusPill status={status.status_name} />
                    {isCurrent && (
                      <span className="move-to-modal__current">
                        <CheckIcon size={14} /> Current
                      </span>
                    )}
                    {locked && <span className="move-to-modal__locked">{MANAGER_ONLY_LABEL}</span>}
                  </button>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </Modal>
  );
};

export default MoveToModal;
